import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarClock, Sparkles, Loader2, CalendarCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const nextMonday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  return d.toISOString().slice(0, 10);
};
const fmtDay = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

const UpcomingTests = ({ items }) => (
  <Card data-testid="upcoming-tests">
    <CardHeader>
      <CardTitle className="flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-blue-700" /> Contrôles à venir</CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      {items.length === 0 && <p className="text-sm text-gray-500">Aucun contrôle planifié.</p>}
      {items.map((t) => (
        <div key={t.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`upcoming-test-${t.id}`}>
          <div>
            <p className="font-medium">{t.title} <span className="text-gray-500 font-normal">· {t.subject_name}</span></p>
            <p className="text-sm text-gray-600">
              {fmtDay(t.date)}{t.start_time ? ` · ${t.start_time}–${t.end_time}` : ''} · {t.class_name}
              {t.children?.length > 0 && ` · ${t.children.join(', ')}`}
            </p>
          </div>
          <Badge className={t.assessment_type === 'EXAM' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'}>
            {t.assessment_type === 'EXAM' ? 'Examen' : 'Contrôle'}
          </Badge>
        </div>
      ))}
    </CardContent>
  </Card>
);

const Proposals = ({ data, picked, onPick }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="test-proposals">
    {data.proposals.map((p, i) => {
      const active = picked && picked.date === p.date && picked.start_time === p.start_time;
      const reco = data.recommended && data.recommended.date === p.date && data.recommended.start_time === p.start_time;
      return (
        <button key={i} type="button" disabled={!p.available} onClick={() => onPick(p)} data-testid={`test-proposal-${i}`}
          className={`text-left border rounded-md p-3 transition-colors ${active ? 'border-blue-600 bg-blue-50' : 'hover:bg-gray-50'} ${!p.available ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <p className="font-medium text-sm flex items-center gap-2">{p.label}{reco && <Badge className="bg-emerald-100 text-emerald-800">Proposé</Badge>}</p>
          <p className="text-xs text-gray-500">{p.available ? (p.room || 'Créneau libre') : p.reason}</p>
        </button>
      );
    })}
  </div>
);

const TestScheduler = ({ onScheduled, user }) => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ class_id: '', subject_id: '', week: nextMonday(), title: '', type: 'TEST', coefficient: 1, description: '' });
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const isTeacher = user.role === 'TEACHER';
    Promise.all([
      axios.get(`${API}/classes`), axios.get(`${API}/subjects`),
      isTeacher ? axios.get(`${API}/timetables/my-schedule`) : Promise.resolve({ data: null }),
    ]).then(([c, s, mine]) => {
      const slots = Array.isArray(mine.data) ? mine.data : null;
      setClasses(slots ? c.data.filter((x) => slots.some((t) => t.class_id === x.id)) : c.data);
      setSubjects(slots ? s.data.filter((x) => slots.some((t) => t.subject_id === x.id)) : s.data);
    }).catch(() => {});
  }, [user.role]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (['class_id', 'subject_id', 'week'].includes(k)) { setData(null); setPicked(null); } };

  const propose = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/tests/propose`, { class_id: form.class_id, subject_id: form.subject_id, week_start: form.week });
      setData(d);
      setPicked(d.recommended);
      if (!d.recommended) toast.info('Aucun créneau libre cette semaine : essayez une autre semaine');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Proposition impossible');
    } finally {
      setBusy(false);
    }
  };

  const schedule = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/tests/schedule`, {
        class_id: form.class_id, subject_id: form.subject_id, test_date: picked.date, start_time: picked.start_time, end_time: picked.end_time,
        title: form.title, description: form.description || null, coefficient: Number(form.coefficient) || 1, assessment_type: form.type,
      });
      toast.success(d.message);
      setData(null); setPicked(null); setForm((f) => ({ ...f, title: '', description: '' }));
      onScheduled();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Planification impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="test-scheduler">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-orange-600" /> Planifier un contrôle</CardTitle>
        <CardDescription>Choisissez la classe, la matière et la semaine : EduScope propose un cours de l’emploi du temps en évitant les jours où la classe a déjà un contrôle. Élèves et parents sont prévenus dans l’application et par email.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1"><Label>Classe</Label>
            <Select value={form.class_id} onValueChange={(v) => set('class_id', v)}>
              <SelectTrigger data-testid="test-class-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Matière</Label>
            <Select value={form.subject_id} onValueChange={(v) => set('subject_id', v)}>
              <SelectTrigger data-testid="test-subject-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Semaine du</Label>
            <Input type="date" lang="fr" value={form.week} onChange={(e) => set('week', e.target.value)} data-testid="test-week-input" /></div>
        </div>
        <Button onClick={propose} disabled={busy || !form.class_id || !form.subject_id || !form.week} variant="outline" data-testid="test-propose-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Proposer une date
        </Button>
        {data && <Proposals data={data} picked={picked} onPick={setPicked} />}
        {picked && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-1 md:col-span-2"><Label>Titre</Label>
              <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Ex : Contrôle chapitre 3" data-testid="test-title-input" /></div>
            <div className="space-y-1"><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger data-testid="test-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="TEST">Contrôle</SelectItem><SelectItem value="EXAM">Examen</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Coefficient</Label>
              <Input type="number" min="0.5" step="0.5" value={form.coefficient} onChange={(e) => set('coefficient', e.target.value)} data-testid="test-coef-input" /></div>
            <div className="space-y-1 md:col-span-4"><Label>Consignes (facultatif)</Label>
              <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Chapitres à réviser, matériel autorisé…" data-testid="test-description-input" /></div>
            <Button onClick={schedule} disabled={busy || !form.title.trim()} className="bg-blue-700 hover:bg-blue-800 md:col-span-4" data-testid="test-schedule-btn">
              Planifier le {fmtDay(picked.date)} à {picked.start_time} et prévenir élèves et parents
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function TestsPage({ user }) {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/tests/upcoming`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="p-6 space-y-6" data-testid="tests-page">
      <h1 className="text-2xl font-bold">Contrôles</h1>
      {['TEACHER', 'ADMIN'].includes(user.role) && <TestScheduler onScheduled={load} user={user} />}
      <UpcomingTests items={items} />
    </div>
  );
}
